"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/auth-context";
import { ApiError, lookupEmail } from "@/lib/api/api-client";
import { countPendingClaims } from "@/lib/library/pending-claims";
import { authOpeningEventName, track } from "@/lib/analytics";
import type { AuthTrigger } from "@/lib/analytics/events";
import { useModalShell } from "@/hooks/use-modal-shell";
import { useIsCompact } from "@/hooks/use-is-compact";
import { ACCOUNT_COLORS, ACCOUNT_EASING } from "./account-palette";
import { UnderlineField } from "./underline-field";

/**
 * L'écran de compte, en pleine page.
 *
 * ## Pourquoi pas une carte
 *
 * Un champ seul dans 440 px, ce n'est pas de l'air, c'est du vide : la
 * composition éditoriale a besoin d'espace pour exister, et une carte n'en a
 * pas. Le dépouillement — un titre, un champ, une ligne — ne se lit comme un
 * parti pris que s'il occupe l'écran ; dans une boîte il se lit comme un
 * formulaire inachevé.
 *
 * ## Pourquoi pas une route pour autant
 *
 * Naviguer démonterait le Studio : Pixi neuf, graphe audio détruit, parcours
 * réinitialisé. C'est précisément ce qu'on a refusé au lien magique, et la
 * raison ne change pas parce que la surface grandit.
 *
 * D'où ce montage : `fixed inset-0`, exactement comme `WelcomeStudioCard`. Le
 * Studio reste monté et faiblement visible derrière le voile — ce qui dit,
 * sans un mot, que rien n'a été perdu et qu'on va y revenir. Fermer rend la
 * main à l'identique.
 *
 * ## Le parcours
 *
 * L'adresse d'abord, seule. `POST /api/auth/lookup` décide de la suite, si bien
 * qu'on ne demande jamais « tu t'inscris ou tu te connectes ? » — la seule
 * question d'un formulaire à laquelle on ne peut pas répondre de mémoire.
 *
 * Et c'est le découpage qui rend le rendu possible, pas l'inverse : « plus
 * gros, sans boîte » n'existe que si le champ est seul à l'écran.
 */

type AccountScreenProps = {
  open: boolean;
  onClose: () => void;
  /** D'où vient la demande. Segmente la conversion par point d'entrée. */
  trigger: AuthTrigger;
};

type Step = "email" | "password" | "create";

const MIN_PASSWORD_LENGTH = 8;
const SUPPORT_EMAIL = "contact@virtuosa.io";

const DEFAULT_EMAIL_TITLE = "Retrouve tes morceaux.";

/**
 * La promesse change avec la porte par laquelle on arrive : quelqu'un venu
 * d'un lien de récital ne cherche pas ses morceaux, il veut entrer dans la
 * salle. Un point d'entrée absent garde la promesse par défaut.
 *
 * `live_session` est la jointure depuis le studio. Depuis le 2026-09-14, tout
 * live est un récital, et on y entre pour regarder et écouter : « Joue en
 * live » promettait de jouer à quelqu'un qui venait simplement participer.
 *
 * `live_jam` est l'autre live : « Inviter », « Rejoindre », ou le lien d'une
 * séance reçu de quelqu'un. Là, on vient jouer, et la promesse le dit.
 *
 * Et les deux côtés du récital ne se confondent pas : « Réserve ta place »
 * accueillait aussi celui qui venait d'en programmer un, à qui on promettait
 * un siège dans la salle au lieu de la scène.
 */
const EMAIL_TITLE_BY_TRIGGER: Partial<Record<AuthTrigger, string>> = {
  live_session: "Rejoins le récital.",
  live_jam: "Joue à plusieurs.",
  recital: "Réserve ta place.",
  recital_schedule: "Monte sur scène.",
  recital_record: "Garde ce que tu joues.",
};

/**
 * L'action principale : l'indication elle-même **est** le bouton.
 *
 * Le pavé, puis le texte-chevron, ont tous deux été retirés — sur une
 * composition qui n'a plus aucune boîte, l'action doit être la chose la plus
 * légère de l'écran, pas la plus lourde. Reste l'idiome de la carte de
 * bienvenue, en mono espacé.
 *
 * Mais c'est un vrai `<button type="submit">`, et ce n'est pas un détail. Sans
 * bouton de soumission, la spécification HTML n'autorise la soumission
 * implicite par `Entrée` que si le formulaire ne contient **qu'un seul** champ
 * qui la bloque. C'est vrai à l'étape de l'adresse, faux à celle du mot de
 * passe : le champ `username` masqué qu'on y ajoute pour les gestionnaires de
 * mots de passe en fait deux. Un écran qui affiche « PRESSE ↵ » et où `Entrée`
 * ne fait rien serait la pire des deux options.
 *
 * Et un vrai bouton reste tapable au doigt, là où une indication clavier ne
 * dit rien à quelqu'un sur tablette.
 */
function InlineSubmit({ label, busy }: { label: string; busy: boolean }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="submit"
      disabled={busy}
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer disabled:cursor-default"
      style={{
        marginTop: 48,
        padding: "10px 14px",
        background: "transparent",
        border: "none",
        fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.28em",
        color: hovered && !busy ? ACCOUNT_COLORS.textLabelActive : ACCOUNT_COLORS.textMuted,
        transition: `color 180ms ${ACCOUNT_EASING}`,
      }}
    >
      {busy ? "· · ·" : "PRESSE ↵ POUR CONTINUER"}
    </button>
  );
}

export function AccountScreen({ open, onClose, trigger }: AccountScreenProps) {
  const { signIn, register } = useAuth();
  const compact = useIsCompact();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [revealPassword, setRevealPassword] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const screenRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const openedAtRef = useRef(0);

  const backToEmail = useCallback(() => {
    setStep("email");
    setPassword("");
    setFieldError(null);
    setFormError(null);
    setRevealPassword(false);
  }, []);

  const dismiss = useCallback(() => {
    track({
      name: "auth_prompt_dismissed",
      properties: { trigger, hadEmail: email.trim() !== "" },
    });
    onClose();
  }, [trigger, email, onClose]);

  // `Escape` revient à l'adresse avant de fermer : sur le second écran elle est
  // déjà tapée, la jeter d'un coup serait brutal.
  const handleEscape = useCallback(() => {
    if (step !== "email") {
      backToEmail();

      return;
    }

    dismiss();
  }, [step, backToEmail, dismiss]);

  useModalShell(screenRef, open, handleEscape);

  useEffect(() => {
    if (!open) {
      return;
    }

    openedAtRef.current = Date.now();
    setStep("email");
    setFieldError(null);
    setFormError(null);
    setPendingCount(countPendingClaims());
    setPassword("");
    setRevealPassword(false);

    // Pas toujours `auth_prompt_shown` : quand une surface a déjà proposé le
    // compte, cette ouverture est un clic et non une exposition.
    track({ name: authOpeningEventName(trigger), properties: { trigger } });
  }, [open, trigger]);

  // Le voile verrouille le défilement du corps, comme le fait `ModalOverlay`
  // pour les modales : sans ça, la page continue de défiler derrière.
  useEffect(() => {
    if (!open) {
      return;
    }

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (step === "email") {
      return;
    }

    const handle = window.setTimeout(() => passwordRef.current?.focus(), 60);

    return () => window.clearTimeout(handle);
  }, [step]);

  function reportFailure(err: unknown): void {
    if (!(err instanceof ApiError)) {
      setFormError("Pas de connexion au serveur. Ton morceau et ta session ne bougent pas.");
      track({ name: "auth_failed", properties: { reason: "network" } });

      return;
    }

    if (err.status === 429) {
      setFormError("Trop de tentatives. Réessaie dans quelques minutes.");
      track({ name: "auth_failed", properties: { reason: "throttled" } });

      return;
    }

    if (err.status === 401) {
      // Vrai ici, et seulement ici : `lookup` vient d'établir que le compte
      // existe, donc le dire ne révèle rien de plus.
      setPassword("");
      setFieldError("Ce n'est pas le bon mot de passe.");
      window.setTimeout(() => passwordRef.current?.focus(), 0);
      track({ name: "auth_failed", properties: { reason: "invalid_credentials" } });

      return;
    }

    if (err.status === 422) {
      // La même cause que nos propres refus de saisie, simplement tranchée par
      // le serveur. Rangée sous `network`, une adresse refusée se lisait comme
      // une panne et gonflait le seul motif sur lequel on ne peut rien.
      setFormError("Quelque chose n'a pas fonctionné. Réessaie.");
      track({ name: "auth_failed", properties: { reason: "validation" } });

      return;
    }

    // Ce qui reste — un 409 sur une adresse déjà prise, un 5xx — n'a pas de
    // valeur à lui dans `AuthFailureReason` et tombe faute de mieux sous
    // `network`, qui le décrit mal : ni l'un ni l'autre n'est une panne de
    // connexion.
    setFormError("Quelque chose n'a pas fonctionné. Réessaie.");
    track({ name: "auth_failed", properties: { reason: "network" } });
  }

  const submitEmail = useCallback(async () => {
    if (!email.includes("@") || email.startsWith("@") || email.endsWith("@")) {
      setFieldError("Il manque quelque chose autour du @.");
      track({ name: "auth_failed", properties: { reason: "validation" } });

      return;
    }

    setIsSubmitting(true);

    try {
      const known = await lookupEmail(email);
      setFieldError(null);
      setStep(known ? "password" : "create");
    } catch (err) {
      reportFailure(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [email]);

  const submitPassword = useCallback(async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(`${MIN_PASSWORD_LENGTH} caractères minimum.`);
      track({ name: "auth_failed", properties: { reason: "validation" } });

      return;
    }

    setIsSubmitting(true);

    try {
      if (step === "create") {
        await register(email, password);
        track({
          name: "account_registered",
          properties: { trigger, durationMs: Date.now() - openedAtRef.current },
        });
        onClose();

        return;
      }

      const outcome = await signIn(email, password);

      // `signIn` peut rendre la main sans avoir ouvert de session : le compte a
      // disparu entre le `lookup` et l'envoi du mot de passe. On comptait quand
      // même une connexion, et on fermait l'écran sur quelqu'un resté anonyme —
      // deux mensonges pour le prix d'un. La création est le chemin qui reste.
      if (outcome === "unknown-email") {
        setStep("create");

        return;
      }

      track({
        name: "account_logged_in",
        properties: { trigger, durationMs: Date.now() - openedAtRef.current },
      });
      onClose();
    } catch (err) {
      reportFailure(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [step, email, password, register, signIn, trigger, onClose]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      void (step === "email" ? submitEmail() : submitPassword());
    },
    [step, submitEmail, submitPassword],
  );

  if (!open) {
    return null;
  }

  const isCreating = step === "create";
  const onEmailStep = step === "email";

  // Une seule ligne, et elle dit la promesse plutôt que la mécanique.
  //
  // L'écran empilait un kicker « TON COMPTE », un titre « Ton compte. » et une
  // sous-ligne qui expliquait le fonctionnement — « connexion et création,
  // c'est le même écran ». Trois fois la même information, dont deux dont
  // personne n'a envie. Le dépouillement du dessin vaut aussi pour le texte :
  // s'il y a une seule phrase, elle doit être la bonne.
  const title = onEmailStep
    ? (EMAIL_TITLE_BY_TRIGGER[trigger] ?? DEFAULT_EMAIL_TITLE)
    : isCreating
      ? "Première fois ici."
      : "Content de te revoir.";

  const cta = onEmailStep ? "Continuer" : isCreating ? "Créer mon compte" : "Se connecter";

  // La ligne de saisie plafonne à 480 : au-delà, une adresse de trente
  // caractères tient encore mais l'œil doit balayer trop loin entre la fin du
  // texte et le filet.
  const fieldWidth = compact ? 280 : 480;
  const fieldSize = compact ? 20 : 26;

  return (
    <div
      ref={screenRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-screen-title"
      tabIndex={-1}
      className="fixed inset-0 z-[90] outline-none account-screen-enter"
      style={{ fontFamily: "var(--font-inter), Inter, sans-serif" }}
    >
      {/* Le fond, opaque.

          Il reprenait le voile de la carte de bienvenue — 0,78 d'alpha au
          centre, là où vit justement le contenu. Sur cette carte-là ça
          fonctionne, parce qu'elle est posée sur un piano-roll vide ; ici le
          Studio a un morceau chargé, et le texte du studio transparaissait
          derrière le titre et le champ. Un écran dont tout l'argument est le
          dépouillement ne peut pas avoir du bruit lisible dessous.

          Le dégradé reste, mais en couleur pure : du plus clair au centre vers
          le plus sombre aux bords, ce qui donne la profondeur que le voile
          apportait, sans rien laisser passer. Plus de `backdrop-filter` non
          plus — flouter ce qu'on ne voit plus coûte du GPU pour rien, sur un
          produit qui parke justement son ticker au repos. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 45%, #101017 0%, #0a0a0f 55%, #07070b 100%)",
        }}
      />

      {/* Le halo violet. Il existait déjà mais se noyait dans le voile
          translucide ; sur un fond opaque il porte enfin. Deux nappes : une
          large et diffuse qui baigne la composition, une plus resserrée sous
          le champ, là où le regard se pose. */}
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) translateY(-120px)",
          width: 900,
          height: 520,
          background: "radial-gradient(ellipse, rgba(139,92,246,0.20) 0%, rgba(139,92,246,0.06) 45%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) translateY(40px)",
          width: 520,
          height: 240,
          background: "radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, transparent 68%)",
        }}
      />

      {/* Retour, seulement là où il y a un pas en arrière à faire. */}
      {!onEmailStep ? (
        <button
          type="button"
          onClick={backToEmail}
          aria-label="Revenir à l'adresse"
          className="absolute left-5 top-5 z-10 w-9 h-9 grid place-items-center rounded-lg hover:bg-white/[0.06] active:scale-95 transition-all duration-200 cursor-pointer"
          style={{ color: ACCOUNT_COLORS.textMuted }}
        >
          <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.5 3.5 5 8l4.5 4.5" />
          </svg>
        </button>
      ) : null}

      <button
        type="button"
        onClick={dismiss}
        aria-label="Fermer"
        className="absolute right-5 top-5 z-10 w-9 h-9 grid place-items-center rounded-lg hover:bg-white/[0.06] hover:rotate-90 active:scale-95 transition-all duration-200 cursor-pointer"
        style={{ color: ACCOUNT_COLORS.textMuted }}
      >
        <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>

      <div className="absolute inset-0 flex items-center justify-center px-6 overflow-y-auto">
        <form onSubmit={handleSubmit} className="flex flex-col items-center text-center py-12">
          <h1
            id="account-screen-title"
            className="m-0"
            style={{
              fontFamily: "var(--font-fraunces), serif",
              fontWeight: 300,
              fontSize: compact ? 38 : 56,
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              color: ACCOUNT_COLORS.text,
              marginBottom: 48,
            }}
          >
            {title}
          </h1>

          {/* Le champ, centré comme le reste. La contrepartie est réelle : une
              adresse plus longue que la ligne défile depuis le centre, ce qui se
              voit davantage qu'un défilement par la fin. À 26 px sur 480, il
              faut passer trente caractères pour y arriver. */}
          <div style={{ width: fieldWidth, maxWidth: "100%" }}>
            {!onEmailStep ? (
              <>
                <div className="flex flex-col items-center" style={{ marginBottom: 32 }}>
                  <span
                    className="truncate max-w-full"
                    style={{ fontSize: compact ? 15 : 17, color: ACCOUNT_COLORS.textSecondary }}
                  >
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={backToEmail}
                    className="text-[12px] cursor-pointer hover:underline"
                    style={{ marginTop: 6, color: ACCOUNT_COLORS.textMuted }}
                  >
                    Modifier
                  </button>
                </div>

                {/* Les gestionnaires de mots de passe veulent l'identifiant et
                    le secret dans le même formulaire. Masqué visuellement,
                    jamais en `display: none` ni `hidden` — ils les ignorent. */}
                <input
                  type="text"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                  autoComplete="username"
                  style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
                />

                <UnderlineField
                  value={password}
                  onChange={(next) => {
                    setPassword(next);
                    setFieldError(null);
                  }}
                  type={revealPassword ? "text" : "password"}
                  autoComplete={isCreating ? "new-password" : "current-password"}
                  inputRef={passwordRef}
                  hint={isCreating ? `${MIN_PASSWORD_LENGTH} caractères minimum` : undefined}
                  error={fieldError}
                  fontSize={fieldSize}
                  align="center"
                  trailing={
                    password !== "" ? (
                      <button
                        type="button"
                        onClick={() => setRevealPassword((shown) => !shown)}
                        aria-label={revealPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        className="w-8 h-8 grid place-items-center rounded-md hover:bg-white/[0.06] transition-colors cursor-pointer"
                        style={{ color: ACCOUNT_COLORS.textMuted }}
                      >
                        <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                          <path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8Z" />
                          <circle cx="8" cy="8" r="1.9" />
                          {revealPassword ? <path d="M2.5 13.5 13.5 2.5" strokeLinecap="round" /> : null}
                        </svg>
                      </button>
                    ) : null
                  }
                />
              </>
            ) : (
              <UnderlineField
                value={email}
                onChange={(next) => {
                  setEmail(next);
                  setFieldError(null);
                }}
                type="email"
                inputMode="email"
                placeholder="ton@email.com"
                autoComplete="username"
                autoFocus
                error={fieldError}
                fontSize={fieldSize}
                align="center"
              />
            )}
          </div>

          {/* La preuve concrète, seulement à la création et seulement s'il y a
              réellement quelque chose à rattacher. */}
          {isCreating && pendingCount > 0 ? (
            <p
              className="text-[12px] leading-[18px]"
              style={{
                marginTop: 24,
                padding: "12px 16px",
                borderRadius: 10,
                maxWidth: fieldWidth,
                backgroundColor: ACCOUNT_COLORS.accentSubtle,
                color: ACCOUNT_COLORS.textSecondary,
              }}
            >
              {pendingCount === 1
                ? "Ton morceau importé rejoindra ta liste."
                : `Tes ${pendingCount} morceaux importés rejoindront ta liste.`}
            </p>
          ) : null}

          {/* L'état du produit, au seul instant où il engage quelque chose.

              C'est ici, et pas dans les réglages, parce que créer un compte est
              le seul moment où l'on confie quelque chose à Virtuosa : la
              question « et si tout disparaissait ? » y est active plutôt que
              théorique. Ailleurs, la pastille du header suffit.

              Dans la typographie de l'écran et non en mono comme sur la carte
              d'accueil : rien ici n'est en chasse fixe, une capitale espacée y
              serait une pièce rapportée. */}
          {isCreating ? (
            <p
              className="text-[11px] leading-[17px]"
              style={{ marginTop: 20, maxWidth: fieldWidth, color: ACCOUNT_COLORS.textMuted }}
            >
              Virtuosa est en bêta. Ton compte et tes morceaux sont conservés.
            </p>
          ) : null}

          {formError !== null ? (
            <p className="text-[12px] leading-[17px]" style={{ marginTop: 20, maxWidth: fieldWidth, color: ACCOUNT_COLORS.error }} role="alert">
              {formError}
            </p>
          ) : null}

          <InlineSubmit label={cta} busy={isSubmitting} />

          {step === "password" ? (
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Mot de passe oublié")}`}
              className="text-[11px] hover:underline"
              style={{
                marginTop: 28,
                color: fieldError !== null ? ACCOUNT_COLORS.textLabelActive : ACCOUNT_COLORS.textMuted,
              }}
            >
              Mot de passe oublié ?
            </a>
          ) : null}

          {isCreating ? (
            <p
              className="text-[11px] leading-[17px]"
              style={{ marginTop: 28, maxWidth: 360, color: ACCOUNT_COLORS.textMuted }}
            >
              {/*
                Une phrase et deux liens, juste au-dessus du geste, plutôt
                qu'une case à cocher. La case n'est pas exigée pour accepter des
                conditions générales, elle ajouterait un clic sur l'écran qui
                vaut par sa nudité, et surtout elle ne prouverait pas *quoi* a
                été coché. Ce qui le prouve, c'est la version enregistrée sur le
                compte au moment de l'inscription.
              */}
              En continuant tu acceptes les{" "}
              <a href="/conditions" className="underline hover:text-white/70">
                conditions générales
              </a>{" "}
              et la{" "}
              <a href="/confidentialite" className="underline hover:text-white/70">
                politique de confidentialité
              </a>
              .
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
